import React, { useState, useEffect } from 'react';
import {
  Brain,
  Shield,
  Zap,
  Users,
  Target,
  Lightbulb,
  Globe,
  Rocket,
  Heart,
  Star,
  ArrowRight,
  Sparkles,
  Code,
  Database,
  ChevronRight,
  Award,
  TrendingUp,
  Clock,
  CheckCircle,
  MessageSquare,
  Bot
} from 'lucide-react';

const AboutUsBody = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [activeValueIndex, setActiveValueIndex] = useState(0);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveValueIndex((prev) => (prev + 1) % values.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const values = [
    {
      icon: <Brain className="w-8 h-8" />,
      title: 'Innovation First',
      description: 'We push the boundaries of what\'s possible with AI, constantly exploring new frontiers in agent intelligence and automation.',
      color: 'from-purple-500 to-blue-500'
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Human-Centered AI',
      description: 'Our technology augments human capabilities rather than replacing them, creating symbiotic relationships between humans and AI.',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Trust & Security',
      description: 'Enterprise-grade security and privacy are built into every aspect of our platform from day one.',
      color: 'from-green-500 to-blue-500'
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: 'Simplicity at Scale',
      description: 'Complex AI capabilities delivered through intuitive interfaces that anyone can use to build powerful automation.',
      color: 'from-yellow-500 to-orange-500'
    }
  ];

  const timeline = [
    {
      year: '2024',
      title: 'The Vision',
      description: 'Founded with the mission to democratize AI agent technology and make automation accessible to everyone.'
    },
    {
      year: '2025',
      title: 'Platform Launch',
      description: 'Released SelficAI with multi-LLM support, visual agent builder, and enterprise security features.'
    }
  ];

  const teamMembers = [
    {
      name: 'Hassan Zamir',
      role: 'CEO & Co-Founder',
      background: 'Former Consultant at Neom',
      image: '/api/placeholder/120/120',
      specialty: 'Project Management & AI Strategy'
    },
    {
      name: 'Ahmad Mukhtar',
      role: 'CTO & Co-Founder', 
      background: 'AI Architect with 10+ years in AI startups',
      image: '/api/placeholder/120/120',
      specialty: 'AI Architecture & Engineering'
    }
  ];

  const stats = [
    { number: '500+', label: 'Active Users', icon: <Users className="w-6 h-6" /> },
    { number: '1000+', label: 'Workflows Automated', icon: <Bot className="w-6 h-6" /> },
    { number: '99.9%', label: 'Uptime', icon: <Shield className="w-6 h-6" /> },
    { number: '5+', label: 'LLM Integrations', icon: <Brain className="w-6 h-6" /> }
  ];

  const achievements = [
    { title: 'Y Combinator S23', icon: <Award className="w-5 h-5" /> },
    { title: 'SOC 2 Certified', icon: <Shield className="w-5 h-5" /> },
    { title: 'AI Innovation Award 2024', icon: <Star className="w-5 h-5" /> },
    { title: '$10M Series A', icon: <TrendingUp className="w-5 h-5" /> }
  ];

  return (
    <main className="bg-black text-white font-sans">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            background: `radial-gradient(600px circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(59, 130, 246, 0.15), transparent 40%)`
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-transparent to-blue-900/20" />
      </div>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-purple-600/20 to-blue-600/20 border border-purple-500/30 rounded-full px-4 py-2 mb-6">
            <Sparkles className="w-4 h-4 text-purple-400" />
            <span className="text-sm text-purple-300">Our Story</span>
          </div>
          
          <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-white via-purple-200 to-blue-200 bg-clip-text text-transparent">
            Building the Future of
            <span className="block mt-2">Intelligent Automation</span>
          </h1>
          
          <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed mb-12">
            At SelficAI, we believe that AI should amplify human potential, not replace it. 
            We're on a mission to democratize access to intelligent automation, making it possible 
            for anyone to build sophisticated AI agents that transform how work gets done.
          </p>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {stats.map((stat, index) => (
              <div key={index} className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-white/20 rounded-2xl p-6 text-center">
                <div className="text-purple-400 mb-2 flex justify-center">
                  {stat.icon}
                </div>
                <div className="text-2xl md:text-3xl font-bold text-white mb-1">{stat.number}</div>
                <div className="text-sm text-gray-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-white to-purple-200 bg-clip-text text-transparent">
                Our Mission
              </h2>
              <p className="text-xl text-gray-300 leading-relaxed mb-8">
                We're democratizing AI automation by making it accessible, powerful, and safe for organizations of all sizes. 
                Our platform bridges the gap between complex AI capabilities and practical business needs.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-green-400 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-white mb-1">Accessible AI for All</h4>
                    <p className="text-gray-400 text-sm">No coding required - build sophisticated agents with our visual interface</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-green-400 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-white mb-1">Enterprise-Grade Security</h4>
                    <p className="text-gray-400 text-sm">Built with security, compliance, and privacy as core principles</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-green-400 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-white mb-1">Human-AI Collaboration</h4>
                    <p className="text-gray-400 text-sm">Augment human capabilities instead of replacing them</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="bg-gradient-to-br from-purple-600/20 to-blue-600/20 backdrop-blur-xl border border-purple-500/30 rounded-3xl p-8">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white/10 rounded-xl p-4 text-center">
                    <Brain className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                    <div className="text-sm font-medium">Multi-LLM</div>
                    <div className="text-xs text-gray-400">Intelligence</div>
                  </div>
                  <div className="bg-white/10 rounded-xl p-4 text-center">
                    <Zap className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
                    <div className="text-sm font-medium">Instant</div>
                    <div className="text-xs text-gray-400">Automation</div>
                  </div>
                  <div className="bg-white/10 rounded-xl p-4 text-center">
                    <Shield className="w-8 h-8 text-green-400 mx-auto mb-2" />
                    <div className="text-sm font-medium">Enterprise</div>
                    <div className="text-xs text-gray-400">Security</div>
                  </div>
                  <div className="bg-white/10 rounded-xl p-4 text-center">
                    <Code className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                    <div className="text-sm font-medium">No-Code</div>
                    <div className="text-xs text-gray-400">Platform</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-white to-purple-200 bg-clip-text text-transparent">
              Our Core Values
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              These principles guide everything we do, from product development to customer relationships.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <div
                key={index}
                className={`group relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-white/20 rounded-2xl p-6 hover:border-purple-500/50 transition-all duration-300 cursor-pointer ${
                  activeValueIndex === index ? 'scale-105 border-purple-500/50' : ''
                }`}
                onMouseEnter={() => setActiveValueIndex(index)}
              >
                <div className={`text-transparent bg-gradient-to-r ${value.color} bg-clip-text mb-4`}>
                  {value.icon}
                </div>
                <h3 className="font-semibold text-white mb-3">{value.title}</h3>
                <p className="text-sm text-gray-400 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-white to-purple-200 bg-clip-text text-transparent">
              Our Journey
            </h2>
            <p className="text-xl text-gray-300">From vision to reality - the SelficAI story</p>
          </div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-purple-500 to-blue-500 rounded-full" />

            <div className="space-y-12">
              {timeline.map((item, index) => (
                <div key={index} className={`flex items-center ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}>
                  <div className="flex-1 px-8">
                    <div className={`bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-white/20 rounded-2xl p-6 ${
                      index % 2 === 0 ? 'text-right' : 'text-left'
                    }`}>
                      <div className="text-purple-400 font-bold text-lg mb-2">{item.year}</div>
                      <h3 className="font-semibold text-white text-xl mb-3">{item.title}</h3>
                      <p className="text-gray-400">{item.description}</p>
                    </div>
                  </div>
                  
                  {/* Timeline Dot */}
                  <div className="relative z-10 w-4 h-4 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full border-4 border-black" />
                  
                  <div className="flex-1 px-8" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-white to-purple-200 bg-clip-text text-transparent">
              Meet Our Team
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              World-class talent united by a shared vision of making AI accessible and beneficial for everyone.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {teamMembers.map((member, index) => (
              <div key={index} className="group bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-white/20 rounded-2xl p-6 hover:border-purple-500/50 transition-all duration-300 hover:scale-105">
                <div className="w-24 h-24 bg-gradient-to-br from-purple-500 to-blue-500 rounded-2xl mx-auto mb-4 flex items-center justify-center">
                  <span className="text-2xl font-bold text-white">{member.name.split(' ').map(n => n[0]).join('')}</span>
                </div>
                <h3 className="font-semibold text-white text-center mb-1">{member.name}</h3>
                <p className="text-purple-400 text-center text-sm mb-3">{member.role}</p>
                <p className="text-gray-400 text-center text-xs leading-relaxed mb-3">{member.background}</p>
                <div className="text-center">
                  <span className="inline-block bg-purple-600/20 text-purple-300 px-3 py-1 rounded-full text-xs">
                    {member.specialty}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* CTA Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-br from-purple-600/20 to-blue-600/20 backdrop-blur-xl border border-purple-500/30 rounded-3xl p-12">
            <Users className="w-12 h-12 text-purple-400 mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-white to-purple-200 bg-clip-text text-transparent">
              Join Us on This Journey
            </h2>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Whether you're looking to automate your workflows, build intelligent agents, or be part of our growing team, 
              we'd love to connect with you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-8 py-4 rounded-xl font-semibold hover:from-purple-700 hover:to-blue-700 transition-all duration-300 flex items-center justify-center space-x-2">
                <span>Start Building</span>
                <ArrowRight className="w-5 h-5" />
              </button>
              <button className="border border-white/20 text-white px-8 py-4 rounded-xl font-semibold hover:bg-white/10 transition-all duration-300 flex items-center justify-center space-x-2">
                <span>View Careers</span>
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default AboutUsBody;